import { Injectable, inject, DOCUMENT } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ScriptLoaderService } from '../sys/script-loader.service';

interface Recaptcha {
  render(
    container: string | HTMLElement,
    parameters: {
      sitekey: string;
      size?: 'normal' | 'compact' | 'invisible';
      theme?: 'light' | 'dark';
      badge?: 'bottomright' | 'bottomleft' | 'inline';
      tabindex?: number;
      callback?: (response: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ): string;
  reset(widgetId: string): void;
}

interface RecaptchaWindow extends Window {
  grecaptchaLoad?: () => void;
  grecaptcha?: Recaptcha;
}

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private readonly _scriptLoader: ScriptLoaderService =
    inject(ScriptLoaderService);
  private readonly _document: Document = inject<Document>(DOCUMENT);

  private _recaptcha?: Promise<Recaptcha>;

  public bootstrap(): void {
    if (environment.recaptchaKey && !this._recaptcha) {
      this._recaptcha = new Promise<Recaptcha>(
        (resolve: (value: Recaptcha | PromiseLike<Recaptcha>) => void) => {
          const window: RecaptchaWindow = this._document
            .defaultView as RecaptchaWindow;

          window.grecaptchaLoad = (): void => {
            resolve(window.grecaptcha as Recaptcha);
          };

          this._scriptLoader
            .load(
              'https://www.google.com/recaptcha/api.js?render=explicit&onload=grecaptchaLoad'
            )
            .subscribe((script?: HTMLScriptElement) => {});
        }
      );
    }
  }

  public render(
    container: string | HTMLElement,
    callback?: (response: string) => void,
    expiredCallback?: () => void,
    errorCallback?: () => void
  ): Promise<string> {
    if (!this._recaptcha) {
      return Promise.reject(new Error('Recaptcha not initialized'));
    }

    return this._recaptcha.then((recaptcha: Recaptcha) => {
      return recaptcha.render(container, {
        sitekey: environment.recaptchaKey as string,
        callback,
        'expired-callback': expiredCallback,
        'error-callback': errorCallback,
      });
    });
  }

  public reset(widgetId: string): void {
    if (!this._recaptcha) {
      throw new Error('Recaptcha not initialized');
    }

    this._recaptcha.then((recaptcha: Recaptcha) => {
      recaptcha.reset(widgetId);
    });
  }
}
